import { useEffect } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Contact.css";
import { useContactForm } from "./contact";
import usePageMeta from "../../hooks/usePageMeta";

import {
  BadgeCheck,
  Bus,
  Car,
  CheckCircle2,
  Globe2,
  Hotel,
  MapPin,
  Plane,
  ShieldCheck,
  Phone,
  Mail,
} from "lucide-react";


/* =========================================================
   CONTACT INFORMATION
   ========================================================= */

const INFO_CARDS = [
  {
    code: "CALL",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.8 19.79 19.79 0 01.01 1.18 2 2 0 012 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
      </svg>
    ),
    label: "Call Us",
    value: "9355471999",
    sub: "Travel enquiries & booking support",
    href: "tel:9355471999",
  },

  {
    code: "MAIL",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>
    ),
    label: "Email Us",
    value: "Holidays@makebeetrip.com",
    sub: "Travel and booking enquiries",
    href: "mailto:Holidays@makebeetrip.com",
  },

  {
    code: "VISIT",
    icon: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0118 0z" />
        <circle cx="12" cy="10" r="3" />
      </svg>
    ),
    label: "Visit Us",
    value: "Vikas Puri, New Delhi",
    sub: "West Delhi – 110018, Delhi, India",
    href: "https://maps.app.goo.gl/vSMhuuz7Ge4hYBfy8",
  },

  {
    code: "WEB",
    icon: <Globe2 size={22} />,
    label: "Visit Our Website",
    value: "makebeetrip.com",
    sub: "Explore our travel services online",
    href: "https://makebeetrip.com/",
  },
];


/* =========================================================
   TRAVEL SERVICES
   ========================================================= */

const SERVICES = [
  {
    icon: <Plane size={17} />,
    label: "Flight Tickets",
  },
  {
    icon: <Hotel size={17} />,
    label: "Hotel Bookings",
  },
  {
    icon: <MapPin size={17} />,
    label: "Holiday Packages",
  },
  {
    icon: <Car size={17} />,
    label: "Cab & Airport Transfers",
  },
  {
    icon: <Bus size={17} />,
    label: "Bus Bookings",
  },
  {
    icon: <BadgeCheck size={17} />,
    label: "Visa Assistance",
  },
];


/* =========================================================
   FORM SERVICES
   ========================================================= */

const FORM_SERVICES = [
  {
    value: "flight",
    label: "Flight",
  },
  {
    value: "hotel",
    label: "Hotel",
  },
  {
    value: "holiday",
    label: "Holiday",
  },
  {
    value: "visa",
    label: "Visa",
  },
];


/* =========================================================
   COMPONENT
   ========================================================= */

export default function ContactUs() {
  usePageMeta(
  "Contact MakeBeeTrip | Phone, Email & Travel Support",
  "Contact MakeBeeTrip for bookings, cancellations and refund help. Call 9355471999, email Holidays@makebeetrip.com or use our online enquiry form.",
  "makebeetrip contact, contact makebeetrip, travel support, booking enquiry, cancellation help, refund support, makebeetrip phone number, makebeetrip email, travel agency contact india"
);
  const {
    values,
    errors,
    status,
    handleChange,
    handleSubmit,
  } = useContactForm();


  /* =========================================================
     AOS + SCROLL
     ========================================================= */

  useEffect(() => {
    const prefersReducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    AOS.init({
      duration: prefersReducedMotion ? 0 : 650,
      once: true,
      easing: "ease-out-cubic",
      offset: 60,
    });

    window.scrollTo(0, 0);
  }, []);


  return (
    <main className="contact-page">


      {/* =====================================================
          HERO
      ====================================================== */}

      <section className="contact-hero">

        <div
          className="contact-hero__bg"
          aria-hidden="true"
        >
          <span className="ch-blob ch-blob--1" />
          <span className="ch-blob ch-blob--2" />
          <span className="ch-blob ch-blob--3" />

          <div className="ch-grid" />

          <svg
            className="ch-route"
            viewBox="0 0 1000 300"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <path
              id="ch-route-path"
              d="M -40 230 C 180 40, 420 320, 620 120 S 980 40, 1060 90"
              fill="none"
            />
          </svg>

          <div className="ch-route-plane">
            <Plane size={20} />
          </div>
        </div>


        <div className="contact-hero__inner">

          <span
            className="contact-hero__badge"
            data-aos="fade-down"
          >
            <Plane size={16} />
            MakeBeeTrip Private Limited
          </span>


          <h1
            className="contact-hero__title"
            data-aos="fade-up"
            data-aos-delay="80"
          >
            Get in <em>Touch</em> with MakeBeeTrip
          </h1>


          <p
            className="contact-hero__sub"
            data-aos="fade-up"
            data-aos-delay="160"
          >
            Have a question about your next trip, booking or holiday
            package?
            <br />
            Our travel team is here to assist you.
          </p>

        </div>

      </section>



      {/* =====================================================
          CONTACT INFORMATION
      ====================================================== */}

      <section className="contact-cards">

        <div className="contact-cards__grid">

          {INFO_CARDS.map((card, i) => (

            <div
              key={card.label}
              className="contact-card"
              data-aos="fade-up"
              data-aos-delay={i * 80}
            >

              <span className="contact-card__code">
                {card.code}
              </span>


              <div className="contact-card__icon">
                {card.icon}
              </div>


              <p className="contact-card__label">
                {card.label}
              </p>


              {card.href ? (

                <a
                  className="contact-card__value"
                  href={card.href}
                  target={
                    card.href.startsWith("http")
                      ? "_blank"
                      : undefined
                  }
                  rel={
                    card.href.startsWith("http")
                      ? "noreferrer"
                      : undefined
                  }
                >
                  {card.value}
                </a>

              ) : (

                <span className="contact-card__value">
                  {card.value}
                </span>

              )}


              <p className="contact-card__sub">
                {card.sub}
              </p>

            </div>

          ))}

        </div>

      </section>



      {/* =====================================================
          INTRODUCTION + TRAVEL SERVICES
      ====================================================== */}

      <section className="contact-services-section">

        <div className="contact-services-section__inner">

          <div
            className="contact-services-intro"
            data-aos="fade-up"
          >

            <span className="contact-section-kicker">
              Contact MakeBeeTrip
            </span>


            <h2 className="contact-services-intro__title">
              Travel assistance for your journey
            </h2>


            <p className="contact-services-intro__text">
              Whether you need help with Flights, Hotels, Holiday
              Packages, Cabs, Bus Bookings or Visa Assistance, you can
              contact MakeBeeTrip for travel enquiries and booking
              support.
            </p>

          </div>


          <div className="contact-services-grid">

            {SERVICES.map((service, index) => (

              <div
                className="contact-service"
                key={service.label}
                data-aos="fade-up"
                data-aos-delay={index * 50}
              >

                <span className="contact-service__icon">
                  {service.icon}
                </span>

                <span>
                  {service.label}
                </span>

              </div>

            ))}

          </div>

        </div>

      </section>



      {/* =====================================================
          CONTACT FORM + GOOGLE MAP
      ====================================================== */}

      <section className="contact-main">

        <div className="contact-main__inner">


          {/* =================================================
              LEFT — CONTACT FORM
          ================================================= */}

          <div
            className="contact-form-wrap"
            data-aos="fade-right"
          >

            <div className="contact-form-wrap__header">

              <span className="contact-section-kicker">
                Send Us Your Travel Requirement
              </span>


              <h2 className="contact-form-wrap__title">
                Tell us what you need
              </h2>


              <p className="contact-form-wrap__sub">
                For faster assistance, please include your
                destination, travel date, number of travellers,
                departure city and required service in your message.
              </p>

            </div>


            <form
              className="contact-form"
              onSubmit={handleSubmit}
              noValidate
            >


              {/* NAME + EMAIL */}

              <div className="cf-row">

                <div
                  className={`cf-field ${
                    errors.name
                      ? "cf-field--error"
                      : ""
                  }`}
                >

                  <label htmlFor="name">
                    Full Name
                  </label>


                  <div className="cf-field__input-wrap">

                    <svg
                      className="cf-field__icon"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <circle
                        cx="10"
                        cy="7"
                        r="3.5"
                      />

                      <path
                        d="M3 17c0-3.314 3.134-6 7-6s7 2.686 7 6"
                        strokeLinecap="round"
                      />
                    </svg>


                    <input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your full name"
                      value={values.name}
                      onChange={handleChange}
                      autoComplete="name"
                    />

                  </div>


                  {errors.name && (
                    <span className="cf-field__error">
                      {errors.name}
                    </span>
                  )}

                </div>



                <div
                  className={`cf-field ${
                    errors.email
                      ? "cf-field--error"
                      : ""
                  }`}
                >

                  <label htmlFor="email">
                    Email Address
                  </label>


                  <div className="cf-field__input-wrap">

                    <svg
                      className="cf-field__icon"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path d="M3 5h14a1 1 0 011 1v8a1 1 0 01-1 1H3a1 1 0 01-1-1V6a1 1 0 011-1z" />

                      <path
                        d="M2 6l8 5 8-5"
                        strokeLinecap="round"
                      />
                    </svg>


                    <input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={values.email}
                      onChange={handleChange}
                      autoComplete="email"
                    />

                  </div>


                  {errors.email && (
                    <span className="cf-field__error">
                      {errors.email}
                    </span>
                  )}

                </div>

              </div>



              {/* PHONE + SERVICE */}

              <div className="cf-row">

                <div
                  className={`cf-field ${
                    errors.phone
                      ? "cf-field--error"
                      : ""
                  }`}
                >

                  <label htmlFor="phone">
                    Phone Number
                  </label>


                  <div className="cf-field__input-wrap">

                    <svg
                      className="cf-field__icon"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path
                        d="M2.5 5.5C2.5 3.8 3.8 2.5 5.5 2.5h.5l2 4-1.5 1.5a11 11 0 005 5L13 12l4 2v.5c0 1.7-1.3 3-3 3C7.1 17.5 2.5 12.9 2.5 5.5z"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>


                    <input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="Your phone number"
                      value={values.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                    />

                  </div>


                  {errors.phone && (
                    <span className="cf-field__error">
                      {errors.phone}
                    </span>
                  )}

                </div>



                <div className="cf-field">

                  <label htmlFor="subject">
                    Required Service
                  </label>


                  <div className="cf-field__input-wrap">

                    <svg
                      className="cf-field__icon"
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.6"
                    >
                      <path
                        d="M4 4h12M4 8h8M4 12h6"
                        strokeLinecap="round"
                      />
                    </svg>


                    <select
                      id="subject"
                      name="subject"
                      value={values.subject}
                      onChange={handleChange}
                    >

                      <option value="">
                        Select a service
                      </option>


                      {FORM_SERVICES.map(
                        (service) => (
                          <option
                            key={service.value}
                            value={service.value}
                          >
                            {service.label}
                          </option>
                        )
                      )}

                    </select>

                  </div>

                </div>

              </div>



              {/* MESSAGE */}

              <div
                className={`cf-field ${
                  errors.message
                    ? "cf-field--error"
                    : ""
                }`}
              >

                <label htmlFor="message">
                  Travel Requirement
                </label>


                <div className="cf-field__input-wrap cf-field__input-wrap--textarea">

                  <svg
                    className="cf-field__icon cf-field__icon--top"
                    viewBox="0 0 20 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                  >
                    <path
                      d="M3 4h14M3 8h10M3 12h7"
                      strokeLinecap="round"
                    />
                  </svg>


                  <textarea
                    id="message"
                    name="message"
                    rows={6}
                    placeholder={`Please include:
Destination:
Travel Date:
Number of Travellers:
Departure City:
Other requirements:`}
                    value={values.message}
                    onChange={handleChange}
                  />

                </div>


                {errors.message && (
                  <span className="cf-field__error">
                    {errors.message}
                  </span>
                )}

              </div>



              {/* SUBMIT */}

              <button
                type="submit"
                className={`cf-submit ${
                  status === "loading"
                    ? "cf-submit--loading"
                    : ""
                } ${
                  status === "success"
                    ? "cf-submit--success"
                    : ""
                }`}
                disabled={
                  status === "loading" ||
                  status === "success"
                }
              >

                {status === "loading" && (
                  <span
                    className="cf-submit__spinner"
                    aria-hidden="true"
                  />
                )}


                {status === "success" ? (

                  <>
                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.2"
                      strokeLinecap="round"
                      className="cf-submit__stamp"
                    >
                      <path d="M4 10l4.5 4.5L16 6" />
                    </svg>

                    Message Sent!
                  </>

                ) : status === "loading" ? (

                  "Sending…"

                ) : (

                  <>
                    Send Travel Requirement

                    <svg
                      viewBox="0 0 20 20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M3 10h14M10 3l7 7-7 7" />
                    </svg>
                  </>

                )}

              </button>


              {status === "error" && (
                <p className="cf-error-msg">
                  Something went wrong. Please try again.
                </p>
              )}

            </form>

          </div>



          {/* =================================================
              RIGHT — GOOGLE MAP
          ================================================= */}

          <div
            className="contact-map-wrap"
            data-aos="fade-left"
          >

            <div className="contact-map-header">

              <span className="contact-section-kicker">
                Find Us
              </span>


              <h2 className="contact-map-wrap__title">
                Our Location
              </h2>


              <p className="contact-map-wrap__sub">
                Visit MakeBeeTrip Private Limited at our
                registered and mailing address in Vikas Puri,
                New Delhi.
              </p>

            </div>


            <div className="contact-map">

              <iframe
                title="MakeBeeTrip Private Limited Location"
                src="https://www.google.com/maps?q=WZ%20A-1%2F1%2C%20G%2FF%20R%2FP%2C%20Vikas%20Puri%2C%20New%20Delhi%2C%20West%20Delhi%20110018%2C%20Delhi%2C%20India&output=embed"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />

            </div>


            <a
              className="contact-map-link"
              href="https://maps.app.goo.gl/vSMhuuz7Ge4hYBfy8"
              target="_blank"
              rel="noopener noreferrer"
            >
              <MapPin size={16} />
              Open location in Google Maps
            </a>

          </div>

        </div>



        {/* =====================================================
            COMPANY INFORMATION + CUSTOMER SUPPORT
        ====================================================== */}

        <div className="contact-info-row">


          {/* =================================================
              COMPANY INFORMATION
          ================================================= */}

          <div
            className="contact-company-card"
            data-aos="fade-up"
          >

            <div className="contact-company-card__header">

              <div className="contact-company-card__icon">
                <Globe2 size={19} />
              </div>


              <div>

                <h2>
                  Company Information
                </h2>

                <p className="contact-company-card__subtitle">
                  MakeBeeTrip Private Limited
                </p>

              </div>

            </div>


            <div className="contact-company-card__details">

              <div className="contact-company-card__item">

                <span className="contact-company-card__label">
                  Legal Name
                </span>

                <strong className="contact-company-card__value">
                  MAKEBEETRIP PRIVATE LIMITED
                </strong>

              </div>


              <div className="contact-company-card__item">

                <span className="contact-company-card__label">
                  CIN
                </span>

                <strong className="contact-company-card__value">
                  U79110DC2026PTC471178
                </strong>

              </div>


              <div className="contact-company-card__item">

                <span className="contact-company-card__label">
                  Registered / Mailing Address
                </span>

                <strong className="contact-company-card__value">
                  WZ A-1/1, G/F R/P, Vikas Puri,
                  New Delhi, West Delhi – 110018,
                  Delhi, India
                </strong>

              </div>

            </div>

          </div>



          {/* =================================================
              CUSTOMER SUPPORT
          ================================================= */}

          <div
            className="contact-support-card"
            data-aos="fade-up"
            data-aos-delay="80"
          >

            <div className="contact-support-card__header">

              <div className="contact-support-card__icon">
                <ShieldCheck size={19} />
              </div>


              <div>

                <h2>
                  Customer Support
                </h2>

                <p className="contact-support-card__subtitle">
                  We're here to help with your travel queries
                </p>

              </div>

            </div>


            <p className="contact-support-card__description">
              For booking-related assistance, please keep your{" "}
              <strong>
                booking reference, PNR, transaction ID
              </strong>{" "}
              or other relevant details available when
              contacting our team.
            </p>


            <p className="contact-support-card__description">
              For cancellation and refund-related queries,
              please refer to our{" "}
              <strong>
                Cancellation & Refund Policy
              </strong>{" "}
              and contact us with your booking details.
            </p>


            <div className="contact-support-card__contacts">


              {/* PHONE */}

              <a
                className="contact-support-card__contact"
                href="tel:9355471999"
              >

                <span className="contact-support-card__contact-icon">
                  <Phone size={15} />
                </span>


                <span className="contact-support-card__contact-content">

                  <span className="contact-support-card__contact-label">
                    Phone
                  </span>

                  <span className="contact-support-card__contact-value">
                    9355471999
                  </span>

                </span>

              </a>



              {/* EMAIL */}

              <a
                className="contact-support-card__contact"
                href="mailto:Holidays@makebeetrip.com"
              >

                <span className="contact-support-card__contact-icon">
                  <Mail size={15} />
                </span>


                <span className="contact-support-card__contact-content">

                  <span className="contact-support-card__contact-label">
                    Email
                  </span>

                  <span className="contact-support-card__contact-value">
                    Holidays@makebeetrip.com
                  </span>

                </span>

              </a>



              {/* WEBSITE */}

              <a
                className="contact-support-card__contact"
                href="https://makebeetrip.com/"
                target="_blank"
                rel="noopener noreferrer"
              >

                <span className="contact-support-card__contact-icon">
                  <Globe2 size={15} />
                </span>


                <span className="contact-support-card__contact-content">

                  <span className="contact-support-card__contact-label">
                    Website
                  </span>

                  <span className="contact-support-card__contact-value">
                    makebeetrip.com
                  </span>

                </span>

              </a>

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
          COMPANY LEGAL INFORMATION
      ====================================================== */}

      <section className="contact-company-section">

        <div className="contact-company-section__inner">

          <div className="contact-company-heading">

            <span className="contact-section-kicker">
              Company Information
            </span>


            <h2>
              MakeBeeTrip Private Limited
            </h2>


            <p>
              MakeBeeTrip is a B2C online travel company based
              in New Delhi, India, offering travel booking and
              holiday planning solutions.
            </p>

          </div>


          <div className="contact-company-details">

            <div>

              <small>
                Legal Name
              </small>

              <strong>
                MAKEBEETRIP PRIVATE LIMITED
              </strong>

            </div>


            <div>

              <small>
                CIN
              </small>

              <strong>
                U79110DC2026PTC471178
              </strong>

            </div>


            <div>

              <small>
                Registered / Mailing Address
              </small>

              <strong>
                WZ A-1/1, G/F R/P, Vikas Puri,
                New Delhi, West Delhi – 110018,
                Delhi, India
              </strong>

            </div>

          </div>

        </div>

      </section>



      {/* =====================================================
          FINAL CTA
      ====================================================== */}

      <section className="contact-final">

        <div className="contact-final__inner">

          <CheckCircle2 size={22} />


          <div>

            <h2>
              Plan Your Journey. Make It Memorable.
            </h2>


            <p>
              Contact MakeBeeTrip for travel enquiries,
              bookings and travel-related assistance.
            </p>

          </div>


          <a href="tel:9355471999">
            Call 9355471999
          </a>

        </div>

      </section>

    </main>
  );
}